/** @jsx jsx */
import { Button, Grid, IconButton, Spacer } from "@cd2/components/build/es6"
import { Fields, Form2 } from "@cd2/forms/build/es6"
import { FormState } from "@cd2/forms/build/es6/Form2/FormState"
import { P } from "@cd2/text/build/es6"
import { css, jsx } from "@emotion/core"
import React, { useCallback } from "react"
import { icons } from "../../constants/icons"
import { spliceArray } from "../../utils/arrays"

type IOption = { key: string; value: string }

export function TextArrayOptionsField({
  field,
  postFieldText,
  inputValidation,
}: {
  field: string
  postFieldText?: (value: string) => React.ReactNode
  inputValidation?: (value: string) => string | undefined
}) {
  const formField = Form2.useField<IOption[]>(field, {
    subscribe: true,
    defaultValue: [],
  })

  const handleInputChange = useCallback(
    (value: string, index: number) => {
      const trimmedValue = value.trim()
      const options = formField.getValue()

      if (!trimmedValue) return

      const isDuplicate = options.some((opt, i) => i !== index && opt.value === trimmedValue)
      if (isDuplicate) {
        formField.setError("This option already exists")
        return
      }

      const validationError = inputValidation?.(trimmedValue)
      if (validationError) {
        formField.setError(validationError)
        return
      }

      const newOptions = [...options]
      newOptions[index] = { ...newOptions[index], value: trimmedValue }
      formField.setValue(newOptions)
      formField.setError(undefined)
    },
    [formField, inputValidation],
  )

  const options = formField.getValue()
  const filledOptions = options.filter(opt => opt.value !== "")

  return (
    <div>
      <Grid columns={[{ fr: 1 }, "auto"]} gap={2}>
        {options.map((option, index) => (
          <React.Fragment key={option.key}>
            <Fields.Text
              value={option.value}
              onChange={value => handleInputChange(value, index)}
              placeholder="Enter option"
            />
            <IconButton
              icon={icons.delete}
              onClick={() => {
                formField.setValue(spliceArray(options, index, 1))
              }}
            />
          </React.Fragment>
        ))}
      </Grid>
      <Spacer />
      <Button
        onClick={() => {
          formField.setValue([...options, { key: Date.now().toString(), value: "" }])
        }}
      >
        Add Option
      </Button>
      {postFieldText && filledOptions.length > 0 && (
        <div
          css={css`
            margin-top: 4px;
          `}
        >
          {filledOptions.map(opt => postFieldText(opt.value))}
        </div>
      )}
      {formField.getError() && <P color="error">{formField.getError()}</P>}
    </div>
  )
}
